import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import * as d3 from 'd3';
import { updateUID } from '../store/actions/config.actions';
import { loadData } from '../store/actions/data.actions';
import { DATA, INITIAL_DATA } from '../store/reducer/data.reducer';
import * as _ from 'lodash';
import { environment } from '../../../environments/environment';
import { NgxXml2jsonService } from 'ngx-xml2json';
import { ApplicationService } from '../../shared/services';

@Injectable({
  providedIn: 'root'
})
export class PartsService {
  private uid: string;
  loadedSVG = {};

  private partsViewSvgUrl: string; // the storage url for future partsview : https://inspectionreport.blob.core.windows.net/partsview
  private partsViewApiUrl: string; // the old api url for partsview (bridge to eshop) : https://nexalesapi.azure-api.net

  constructor(private http: HttpClient,
    private store: Store<{ data: DATA; basket: {} }>,
    private ngxXml2jsonService: NgxXml2jsonService,
    private applicationService: ApplicationService
  ) {}

  private loadXML(uid: string) {
    this.uid = uid;
    return this.http
      .get(`${this.partsViewApiUrl}/partviews/${uid}/xml/${uid}.xml`, {
        headers: new HttpHeaders({
          ignore: 'true',
          'Content-Type': 'application/xml'
        }),
        responseType: 'text'
      })
      .pipe(catchError((error: any) => null));
  }

  loadXMLAndParse(uid: string): Observable<INITIAL_DATA> {
    return this.ensureEnvVar().pipe(
      switchMap(() => this.loadXML(uid)),
      map((xml: any) => {
        const parser = new DOMParser();
        const _xml = parser.parseFromString(xml, 'text/xml');
        return this.ngxXml2jsonService.xmlToJson(_xml);
      }),
      map((response: INITIAL_DATA) => {
        // ngxXml2jsonService.xmlToJson returns an object when a node has only one child
        // we need to convert it to have a consistent structure
        const doc43 = response.doc43;
        if (!doc43 || !doc43.partie_machine) {
          console.error('Invalid XML structure: missing doc43 or partie_machine');
          return response;
        }
        if (!Array.isArray(doc43.partie_machine)) {
          doc43.partie_machine = [doc43.partie_machine];
        }
        doc43.partie_machine.forEach((dataValue) => {
          if (dataValue.page && !Array.isArray(dataValue.page)) {
            dataValue.page = [dataValue.page];
          }
        });
        return response;
      }),
    );
  }

  private ensureEnvVar(): Observable<void> {
    return this.applicationService.getEnvironmentVariables().pipe(
      map((env) => {
        this.partsViewSvgUrl = env.partsViewSvgUrl;
        this.partsViewApiUrl = env.partsViewApiUrl;
        if (!this.partsViewSvgUrl || !this.partsViewApiUrl) {
          throw new Error('Parts View URL is not configured properly.');
        }
      })
    );
  }

  public getPartsViewApiUrl(): Observable<string> {
    return this.ensureEnvVar().pipe(
      map(() => {
        return this.partsViewApiUrl;
      })
    );
  }

  loadSVG(svgNumber: string, uid?: string): Observable<any> {
    return this.ensureEnvVar().pipe(
      switchMap(() => this.loadSVG_internal(svgNumber, uid))
    );
  }

  private loadSVG_internal(svgNumber: string, uid?: string): Observable<any> {
    return forkJoin({
      xml: d3.xml(`${this.partsViewApiUrl}/partviews/${uid ?? this.uid}/svg/${svgNumber}.svg`)
    }).pipe(
      switchMap(({ xml }: any) => {
        const xmlClean = this.cleanSvg(xml);

        const links = xmlClean.querySelectorAll('[href]')[0];
        let link = links?.attributes['href'].value?.trim();

        return forkJoin({
          xml: of(xmlClean),
          img: link && !_.startsWith(link, 'http') ? this.loadIMG(`${this.partsViewApiUrl}/partviews/${uid ?? this.uid}/svg/${link}`) : of(null)
        });
      }),
      catchError((error: any) => of(null))
    );
  }

  cleanSvg(xml: HTMLElement) {
    if (!xml) { return xml; }

    // some svgs have <a xlink:href=""> elements that will trigger a redirection if clicked
    // simple <a> will not
    xml.querySelectorAll('a').forEach(l => {
      if (l.hasAttribute('xlink:href') && !l.getAttribute('xlink:href')) {
        l.removeAttribute('xlink:href');
      }
    });

    // some svg do nothave itemnumber attributes
    xml.querySelectorAll('g').forEach(g => {
      const onclick = g.getAttribute('onclick');
      if (
        onclick &&
        !g.hasAttribute('itemnumber') &&
        (onclick.startsWith('javascript:viewPart') || onclick.startsWith('javascript:parent.vue_piece'))
      ) {
        const regExResult = /viewPart\((\d+)\)/.exec(onclick) ?? /vue_piece\((\d+)\)/.exec(onclick);
        if (regExResult) {
          g.setAttribute('itemnumber', regExResult[1]);
        }
      }
    });

    return xml;
  }

  loadIMG(link: string) {
    return this.http
      .get(link, {
        headers: new HttpHeaders({
          ignore: 'true'
        }),
        responseType: 'blob'
      })
      .pipe(catchError((error: any) => null));
  }

  loadDocument(uid: string) {
    this.store.dispatch(updateUID(this.uid));
    this.store.dispatch(loadData(this.uid));
  }
}
